import React, { Component } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function EngineBayDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Engine Bay Detailing',
    serviceType: 'Engine Bay Cleaning & Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks', 'Ventura County'],
    description: 'Professional mobile engine bay detailing in Ventura County. Safe degreasing, cleaning, and dressing of your engine compartment.'
  };
  const faqs = [{
    question: 'Is engine bay detailing safe for my car?',
    answer: 'Yes. We carefully protect sensitive electrical components and use low-pressure, controlled cleaning with appropriate degreasers. Our experienced technicians know exactly which areas to avoid, keeping your engine safe throughout the process.'
  }, {
    question: 'Why should I get my engine bay detailed?',
    answer: 'A clean engine bay makes it far easier to spot leaks and worn components, helps prevent grime buildup that traps heat, and dramatically improves resale value and presentation when selling your vehicle.'
  }, {
    question: 'How much does engine bay detailing cost in Ventura County?',
    answer: 'Engine bay detailing is often added to a full detail or booked on its own. Pricing depends on the size and condition of the engine bay. Call (818) 381-9905 for a quick quote.'
  }, {
    question: 'Do you come to me for engine bay detailing?',
    answer: 'Yes. Like all our services, engine bay detailing is fully mobile. We bring our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Engine Bay Detailing',
    item: 'https://www.fulldetailpro.com/services/engine-bay-detailing'
  }];
  return <ServicePageLayout title="Engine Bay Detailing Ventura County" subtitle="Safe, professional engine bay cleaning that comes to you. Restore a clean, presentable engine compartment without risking sensitive components." metaDescription="Engine bay detailing in Ventura County. Safe mobile degreasing & cleaning of your engine compartment at your home or office. Call Mike's Pro Detail (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-842" data-mp="1et68s0jamdvl">
      <p data-id="element-843" data-mp="1ssxl7wu5vm7h">
        A clean engine bay is the finishing touch on a truly detailed vehicle.
        Our <strong data-id="element-844" data-mp="2q8beldeq0sjf">engine bay detailing in Ventura County</strong> safely
        removes years of grease, dust, and grime from your engine compartment,
        leaving it clean, presentable, and easier to maintain. We perform this
        service on-site as part of our mobile operation, serving Camarillo,
        Oxnard, Ventura, Thousand Oaks, and the surrounding communities.
      </p>

      <h2 data-id="element-845" data-mp="32qf7ybwxklu6">Our Engine Bay Detailing Process</h2>
      <p data-id="element-846" data-mp="32pm8zx47r0ml">
        Engine bay cleaning requires care and experience. We protect sensitive
        electrical components, use controlled low-pressure rinsing, and apply
        the right products for each surface:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-847" data-mp="3bqtutfpjm8zs">
        <li className="flex items-start" data-id="element-848" data-mp="3gperx69i0ck0">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-849" data-mp="3goc0mx89k83k" />
          <span data-id="element-850" data-mp="3li7y7ykm7sy7">
            <strong data-id="element-851" data-mp="3lhexbe8c7xnz">Component Protection:</strong> We cover and protect exposed
            electrical connections and sensitive parts before any cleaning
            begins.
          </span>
        </li>
        <li className="flex items-start" data-id="element-852" data-mp="3ujej0juarpx9">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-853" data-mp="3b0lbktmpxs8" />
          <span data-id="element-854" data-mp="397bxxrmszgj">
            <strong data-id="element-855" data-mp="3ltxr4xwk3m7y">Degreasing:</strong> Safe, effective degreasers break down
            built-up oil, grease, and road grime throughout the bay.
          </span>
        </li>
        <li className="flex items-start" data-id="element-856" data-mp="3hq9l4jnkoyot">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-857" data-mp="3vka8yiczl1ae" />
          <span data-id="element-858" data-mp="3vllh0udgsv7b">
            <strong data-id="element-859" data-mp="3qkxlbveajq6r">Detail Brushing & Rinsing:</strong> Hand brushing of tight
            areas followed by a controlled, low-pressure rinse.
          </span>
        </li>
        <li className="flex items-start" data-id="element-860" data-mp="32x636b62kixk">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-861" data-mp="2yu9p8xrh5azy" />
          <span data-id="element-862" data-mp="3ct9eo7mt9tx6">
            <strong data-id="element-863" data-mp="3ctslotaxaxwf">Dressing & Protection:</strong> A non-greasy dressing
            restores plastics and hoses to a clean, factory-fresh finish.
          </span>
        </li>
      </ul>

      <h2 data-id="element-864" data-mp="s2s3ghkos6d5">Benefits of a Clean Engine Bay</h2>
      <p data-id="element-865" data-mp="s1qpa48dj3r7">
        Beyond looking great, a detailed engine bay makes it easier to identify
        leaks and worn components, helps prevent grime from trapping heat, and
        significantly boosts your vehicle's resale presentation. It's a smart
        addition to any{' '}
        <Link to="/full-detail" data-id="element-866" data-mp="2a0x3odzeusyp">full car detail</Link>.
      </p>

      <h2 data-id="element-867" data-mp="1w0vsa4lis34z">Related Mobile Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-868" data-mp="1w277r1obuzbx">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-869" data-mp="20zgw0dzzd4y6">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-870" data-mp="2sqmukq01yulc">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-871" data-mp="37nvw2cj2leqs">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-872" data-mp="3m91kr3afq92s">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}